import React, { useEffect, useState } from 'react';
import { useLanguage } from '../lang/useLanguage';

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:8001"
  : `${window.location.protocol}//${window.location.hostname}/api`;

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json"
});

export default function Organization() {
  const { t } = useLanguage();
  const [data, setData] = useState({ positions: [], users: [], personnel: [] });
  const [departments, setDepartments] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: "", code: "", parent_id: "" });
  const [deptSaving, setDeptSaving] = useState(false);
  const [positionForm, setPositionForm] = useState({
    department_id: "",
    sub_department: "",
    title: "",
    position_level: "staff",
    role_key: "",
    sort_order: 0
  });
  const [positionSaving, setPositionSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const loadDepartments = () => {
    return fetch(`${API}/organization/departments`, { headers: headers() })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setDepartments(Array.isArray(d) ? d : []));
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API}/organization/map`, { headers: headers() })
        .then(r => r.ok ? r.json() : Promise.reject(r)),
      loadDepartments()
    ])
      .then(([mapData]) => setData(mapData))
      .catch(() => setErr(t.organizationLoadError))
      .finally(() => setLoading(false));
  }, [t.organizationLoadError]);

  const card = {
    background: "#0f172a",
    border: "1px solid #1e293b",
    borderRadius: 14,
    padding: 18,
    color: "#e2e8f0"
  };

  const table = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13
  };

  const th = {
    textAlign: "left",
    padding: "10px",
    borderBottom: "1px solid #334155",
    color: "#93c5fd"
  };

  const td = {
    padding: "10px",
    borderBottom: "1px solid #1e293b",
    color: "#cbd5e1"
  };

  const input = {
    background: "#020617",
    border: "1px solid #334155",
    borderRadius: 8,
    padding: "10px",
    color: "#e2e8f0"
  };

  const btn = {
    background: "#2563eb",
    border: "none",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700
  };

  const saveDepartment = async () => {
    if (!deptForm.name.trim()) {
      alert(t.organizationDepartmentNameRequired);
      return;
    }

    setDeptSaving(true);
    try {
      const res = await fetch(`${API}/organization/departments`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          name: deptForm.name,
          code: deptForm.code || null,
          parent_id: deptForm.parent_id ? Number(deptForm.parent_id) : null
        })
      });

      if (!res.ok) throw new Error("save failed");

      setDeptForm({ name: "", code: "", parent_id: "" });
      await loadDepartments();
    } catch (e) {
      alert(t.organizationDepartmentSaveFailed);
    } finally {
      setDeptSaving(false);
    }
  };

  const toggleDepartment = async (department) => {
    try {
      const res = await fetch(`${API}/organization/departments/${department.id}/status`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ status: department.is_active ? "passive" : "active" })
      });

      if (!res.ok) throw new Error("status failed");

      await loadDepartments();
    } catch (e) {
      alert(t.organizationDepartmentStatusFailed);
    }
  };

  const savePosition = async () => {
    if (!positionForm.department_id) {
      alert(t.organizationSelectDepartment);
      return;
    }

    if (!positionForm.title.trim()) {
      alert(t.organizationPositionTitleRequired);
      return;
    }

    setPositionSaving(true);
    try {
      const res = await fetch(`${API}/organization/positions`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({
          department_id: Number(positionForm.department_id),
          sub_department: positionForm.sub_department || null,
          title: positionForm.title,
          position_level: positionForm.position_level,
          role_key: positionForm.role_key || null,
          sort_order: Number(positionForm.sort_order || 0)
        })
      });

      if (!res.ok) throw new Error("save failed");

      setPositionForm({
        department_id: "",
        sub_department: "",
        title: "",
        position_level: "staff",
        role_key: "",
        sort_order: 0
      });

      const mapRes = await fetch(`${API}/organization/map`, { headers: headers() });
      if (mapRes.ok) {
        const mapData = await mapRes.json();
        setData(mapData);
      }
    } catch (e) {
      alert(t.organizationPositionSaveFailed);
    } finally {
      setPositionSaving(false);
    }
  };

  const togglePosition = async (position) => {
    try {
      const res = await fetch(`${API}/organization/positions/${position.id}/status`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ status: position.is_active ? "passive" : "active" })
      });

      if (!res.ok) throw new Error("status failed");

      const result = await res.json();

      setData(prev => ({
        ...prev,
        positions: prev.positions.map(p =>
          p.id === position.id
            ? { ...p, is_active: result.is_active }
            : p
        )
      }));
    } catch (e) {
      alert(t.organizationPositionStatusFailed);
    }
  };

  if (loading) return <div style={card}>{t.loading}</div>;
  if (err) return <div style={card}>{err}</div>;

  const unassignedUsers = data.users.filter(u => !u.org_structure_id);
  const unlinkedPersonnel = data.personnel.filter(p => !p.user_id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <h1 style={{ color: "#f8fafc", margin: 0 }}>🏢 {t.organizationTitle}</h1>
        <p style={{ color: "#94a3b8", marginTop: 6 }}>
          {t.organizationSubtitle}
        </p>
      </div>

      <div style={card}>
        <h3>Departman Yönetimi</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 220px 120px", gap: 10, marginBottom: 16 }}>
          <input
            style={input}
            placeholder={t.organizationDepartmentNamePlaceholder}
            value={deptForm.name}
            onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
          />

          <input
            style={input}
            placeholder={t.organizationCodePlaceholder}
            value={deptForm.code}
            onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })}
          />

          <select
            style={input}
            value={deptForm.parent_id}
            onChange={(e) => setDeptForm({ ...deptForm, parent_id: e.target.value })}
          >
            <option value="">Üst departman yok</option>
            {departments.filter(d => d.is_active).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          <button style={btn} onClick={saveDepartment} disabled={deptSaving}>
            {deptSaving ? "Kaydediliyor..." : "+ Ekle"}
          </button>
        </div>

        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Kod</th>
              <th style={th}>Departman</th>
              <th style={th}>Üst Departman</th>
              <th style={th}>Durum</th>
              <th style={th}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {departments.map(d => (
              <tr key={d.id}>
                <td style={td}>{d.code || "-"}</td>
                <td style={td}>{d.name}</td>
                <td style={td}>{d.parent_name || "-"}</td>
                <td style={td}>{d.is_active ? "Aktif" : "Pasif"}</td>
                <td style={td}>
                  <button
                    onClick={() => toggleDepartment(d)}
                    style={{
                      ...btn,
                      padding: "6px 10px",
                      background: d.is_active ? "#f97316" : "#22c55e"
                    }}
                  >
                    {d.is_active ? "Pasif Yap" : "Aktif Yap"}
                  </button>
                </td>
              </tr>
            ))}
            {departments.length === 0 && (
              <tr>
                <td style={td} colSpan="5">Henüz departman yok.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <h3>Pozisyon Yönetimi</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.2fr 1fr 1fr 0.8fr auto", gap: 12, marginBottom: 18 }}>
          <select
            style={input}
            value={positionForm.department_id}
            onChange={(e) => setPositionForm({ ...positionForm, department_id: e.target.value })}
          >
            <option value="">Departman seç</option>
            {departments.filter(d => d.is_active).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          <input
            style={input}
            placeholder={t.organizationSubDepartmentPlaceholder}
            value={positionForm.sub_department}
            onChange={(e) => setPositionForm({ ...positionForm, sub_department: e.target.value })}
          />

          <input
            style={input}
            placeholder={t.organizationPositionTitlePlaceholder}
            value={positionForm.title}
            onChange={(e) => setPositionForm({ ...positionForm, title: e.target.value })}
          />

          <select
            style={input}
            value={positionForm.position_level}
            onChange={(e) => setPositionForm({ ...positionForm, position_level: e.target.value })}
          >
            <option value="staff">staff</option>
            <option value="operator">operator</option>
            <option value="foreman">foreman</option>
            <option value="specialist">specialist</option>
            <option value="manager">manager</option>
            <option value="director">director</option>
          </select>

          <input
            style={input}
            placeholder={t.organizationRoleCodePlaceholder}
            value={positionForm.role_key}
            onChange={(e) => setPositionForm({ ...positionForm, role_key: e.target.value })}
          />

          <input
            style={input}
            type="number"
            placeholder={t.organizationOrderPlaceholder}
            value={positionForm.sort_order}
            onChange={(e) => setPositionForm({ ...positionForm, sort_order: e.target.value })}
          />

          <button style={btn} onClick={savePosition} disabled={positionSaving}>
            {positionSaving ? "Kaydediliyor..." : "+ Pozisyon"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
        <div style={card}><div>{t.organizationPositionsLabel}</div><h2>{data.positions.length}</h2></div>
        <div style={card}><div>{t.organizationUsersLabel}</div><h2>{data.users.length}</h2></div>
        <div style={card}><div>{t.organizationPersonnelLabel}</div><h2>{data.personnel.length}</h2></div>
        <div style={card}><div>{t.organizationUnassignedUsersLabel}</div><h2>{unassignedUsers.length}</h2></div>
      </div>

      <div style={card}>
        <h3>{t.organizationPositionsTitle}</h3>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t.organizationDepartment}</th>
              <th style={th}>{t.organizationSubDepartment}</th>
              <th style={th}>{t.organizationJobTitle}</th>
              <th style={th}>{t.organizationLevel}</th>
              <th style={th}>{t.organizationRole}</th>
              <th style={th}>Durum</th>
              <th style={th}>{t.organizationUser}</th>
              <th style={th}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {data.positions.map(p => (
              <tr key={p.id}>
                <td style={td}>{p.department}</td>
                <td style={td}>{p.sub_department || "-"}</td>
                <td style={td}>{p.title}</td>
                <td style={td}>{p.position_level}</td>
                <td style={td}>{p.role_key}</td>
                <td style={td}>{p.is_active ? "Aktif" : "Pasif"}</td>
                <td style={td}>{p.username || t.organizationUnassigned}</td>
                <td style={td}>
                  <button
                    onClick={() => togglePosition(p)}
                    style={{
                      ...btn,
                      padding: "6px 10px",
                      background: p.is_active ? "#f97316" : "#22c55e"
                    }}
                  >
                    {p.is_active ? "Pasif Yap" : "Aktif Yap"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <h3>{t.organizationUnassignedUsersTitle}</h3>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t.organizationUser}</th>
              <th style={th}>{t.organizationRole}</th>
              <th style={th}>{t.organizationTenant}</th>
            </tr>
          </thead>
          <tbody>
            {unassignedUsers.map(u => (
              <tr key={u.id}>
                <td style={td}>{u.username}</td>
                <td style={td}>{u.role}</td>
                <td style={td}>{u.tenant_id || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <h3>{t.organizationUnlinkedPersonnelTitle}</h3>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>{t.organizationFullName}</th>
              <th style={th}>{t.organizationDepartment}</th>
              <th style={th}>{t.organizationRole}</th>
              <th style={th}>{t.organizationTenant}</th>
            </tr>
          </thead>
          <tbody>
            {unlinkedPersonnel.map(p => (
              <tr key={p.id}>
                <td style={td}>{p.name} {p.surname}</td>
                <td style={td}>{p.department || "-"}</td>
                <td style={td}>{p.role || "-"}</td>
                <td style={td}>{p.tenant_id || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
